import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { logMealFromFavorite } from '@/hooks/useFavorites';
import { fmtNum, mealTypeLabel } from '@/utils/format';
import type { Favorite, MealType } from '@/types';
import { DayStrip } from '@/components/DayStrip';
import { consumedAtFor, dayPhrase } from '@/lib/mealDay';

type InputMode = 'portions' | 'grams';

interface LogFavoriteModalProps {
  open: boolean;
  onClose: () => void;
  favorite: Favorite | null;
  onLogged?: (calories: number) => void;
  initialMealType?: MealType;
}

function getDefaultMealType(): MealType {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  if (h < 21) return 'dinner';
  return 'snack';
}

export function LogFavoriteModal({
  open,
  onClose,
  favorite,
  onLogged,
  initialMealType,
}: LogFavoriteModalProps) {
  const { haptic } = useTelegram();

  const [portions, setPortions] = useState(1);
  const [grams, setGrams] = useState<string>('');
  const [inputMode, setInputMode] = useState<InputMode>('portions');
  const [mealType, setMealType] = useState<MealType>(initialMealType ?? getDefaultMealType());
  const [dayOffset, setDayOffset] = useState(0);
  const [saving, setSaving] = useState(false);

  // Сброс при открытии нового рецепта
  useEffect(() => {
    if (open && favorite) {
      setPortions(1);
      setGrams(favorite.is_recipe ? '' : '100');
      setInputMode(favorite.is_recipe ? 'portions' : 'grams');
      setMealType(initialMealType ?? getDefaultMealType());
      setDayOffset(0);
    }
  }, [open, favorite, initialMealType]);

  // Расчёт КБЖУ в реальном времени
  const computed = useMemo(() => {
    if (!favorite) return { cal: 0, p: 0, f: 0, c: 0, weight: 0 };

    if (favorite.is_recipe) {
      if (inputMode === 'portions') {
        const factor = portions;
        return {
          cal: Math.round((favorite.calories_per_portion ?? 0) * factor),
          p: +((favorite.protein_per_portion ?? 0) * factor).toFixed(1),
          f: +((favorite.fat_per_portion ?? 0) * factor).toFixed(1),
          c: +((favorite.carbs_per_portion ?? 0) * factor).toFixed(1),
          weight: Math.round((favorite.portion_weight_g ?? 0) * factor),
        };
      } else {
        const g = Number(grams) || 0;
        const portionWeight = favorite.portion_weight_g ?? 100;
        const factor = g / portionWeight;
        return {
          cal: Math.round((favorite.calories_per_portion ?? 0) * factor),
          p: +((favorite.protein_per_portion ?? 0) * factor).toFixed(1),
          f: +((favorite.fat_per_portion ?? 0) * factor).toFixed(1),
          c: +((favorite.carbs_per_portion ?? 0) * factor).toFixed(1),
          weight: g,
        };
      }
    } else {
      const g = Number(grams) || 0;
      const factor = g / 100;
      return {
        cal: Math.round((favorite.calories_per_100g ?? 0) * factor),
        p: +((favorite.protein_per_100g ?? 0) * factor).toFixed(1),
        f: +((favorite.fat_per_100g ?? 0) * factor).toFixed(1),
        c: +((favorite.carbs_per_100g ?? 0) * factor).toFixed(1),
        weight: g,
      };
    }
  }, [favorite, portions, grams, inputMode]);

  if (!favorite) return null;

  function adjustPortions(delta: number) {
    haptic('light');
    setPortions((p) => Math.max(0.5, +(p + delta).toFixed(1)));
  }

  async function handleSave() {
    if (!favorite) return;
    if (computed.cal <= 0) {
      alert('Укажи количество');
      return;
    }

    setSaving(true);
    haptic('medium');

    try {
      // Сегодня — «сейчас»; любой другой день — в час этого приёма
      const consumedAt = consumedAtFor(dayOffset, mealType);

      const result = await logMealFromFavorite({
        favorite_id: favorite.id,
        portions: inputMode === 'portions' ? portions : undefined,
        grams: inputMode === 'grams' ? Number(grams) : undefined,
        meal_type: mealType,
        consumed_at: consumedAt,
      });

      if (!result.success) {
        haptic('error');
        alert(`Не удалось добавить: ${result.error ?? 'неизвестная ошибка'}`);
        return;
      }

      haptic('success');
      onLogged?.(computed.cal);
      onClose();
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal — FLEX-COLUMN с разделением scroll и кнопки */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="fixed bottom-16 left-0 right-0 z-50 bg-bg rounded-t-3xl flex flex-col"
            style={{
              maxHeight: 'calc(100vh - 144px)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* СКРОЛЛИРУЕМЫЙ КОНТЕНТ */}
            <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
              <div className="max-w-md mx-auto">
                {/* Drag handle */}
                <div className="w-12 h-1 bg-line rounded-full mx-auto mb-4" />

                {/* Header */}
                <div className="flex items-start gap-3 mb-5">
                  <div className="text-4xl">{favorite.emoji || '🍽'}</div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-xl font-bold text-ink truncate">
                      {favorite.name}
                    </h2>
                    <p className="text-xs text-ink-dim mt-0.5">
                      {favorite.is_recipe
                        ? `${favorite.total_portions ?? 1} порций · ${favorite.calories_per_portion ?? 0} ккал/порция`
                        : `${favorite.calories_per_100g} ккал/100г`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-9 h-9 rounded-xl bg-bg-card border border-line flex items-center justify-center text-ink-muted"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Переключение porc/gram (только для рецептов) */}
                {favorite.is_recipe && (
                  <div className="card-elevated p-1 grid grid-cols-2 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setInputMode('portions');
                        haptic('light');
                      }}
                      className={`py-2 rounded-lg text-sm transition-all ${
                        inputMode === 'portions'
                          ? 'bg-bg text-ink shadow-card'
                          : 'text-ink-muted'
                      }`}
                    >
                      Порции
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setInputMode('grams');
                        if (!grams) {
                          setGrams(String(favorite.portion_weight_g ?? 100));
                        }
                        haptic('light');
                      }}
                      className={`py-2 rounded-lg text-sm transition-all ${
                        inputMode === 'grams'
                          ? 'bg-bg text-ink shadow-card'
                          : 'text-ink-muted'
                      }`}
                    >
                      Граммы
                    </button>
                  </div>
                )}

                {/* Ввод порций или грамм */}
                {inputMode === 'portions' ? (
                  <div className="mb-5">
                    <label className="label mb-2 block">Количество порций</label>
                    <div className="flex items-center gap-3 bg-bg-card border border-line rounded-xl p-2">
                      <button
                        type="button"
                        onClick={() => adjustPortions(-0.5)}
                        disabled={portions <= 0.5}
                        className="w-10 h-10 rounded-lg bg-bg-elevated text-ink-muted flex items-center justify-center disabled:opacity-40 hover:bg-line/40 active:scale-95 transition-all"
                      >
                        <Minus size={16} />
                      </button>
                      <div className="flex-1 text-center">
                        <div className="font-display text-2xl font-bold text-ink">
                          {portions === Math.floor(portions) ? portions : portions.toFixed(1)}
                        </div>
                        <div className="text-[10px] text-ink-dim uppercase tracking-wider">
                          порций
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => adjustPortions(0.5)}
                        className="w-10 h-10 rounded-lg bg-emerald-glow/15 text-emerald-glow flex items-center justify-center hover:bg-emerald-glow/25 active:scale-95 transition-all"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <p className="text-[11px] text-ink-dim mt-2">
                      💡 Можно выбрать половину порции (0.5)
                    </p>
                  </div>
                ) : (
                  <div className="mb-5">
                    <label className="label mb-2 block">Сколько грамм?</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={grams}
                        onChange={(e) => setGrams(e.target.value.replace(/\D/g, ''))}
                        className="input flex-1 text-lg font-display font-bold text-center"
                        placeholder="100"
                        autoFocus
                      />
                      <span className="text-ink-muted text-sm">г</span>
                    </div>
                  </div>
                )}

                {/* Тип приёма */}
                <div className="mb-4">
                  <label className="label mb-2 block">Тип приёма</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).map((mt) => (
                      <button
                        type="button"
                        key={mt}
                        onClick={() => {
                          setMealType(mt);
                          haptic('light');
                        }}
                        className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                          mealType === mt
                            ? 'bg-emerald-glow text-bg'
                            : 'bg-bg-card text-ink-muted border border-line'
                        }`}
                      >
                        {mealTypeLabel[mt]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Когда съел */}
                <div className="mb-5">
                  <label className="label mb-2 block">{dayOffset > 0 ? 'На какой день' : 'Когда'}</label>
                  <DayStrip value={dayOffset} onChange={setDayOffset} bleed />
                </div>

                {/* Итого */}
                <div className="card-elevated p-4 border-emerald-glow/30">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="label">Итого</span>
                    <span className="font-display text-2xl font-bold text-emerald-glow">
                      {fmtNum(computed.cal)}
                      <span className="text-sm text-ink-muted font-body ml-1">ккал</span>
                    </span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-emerald-glow">Б {fmtNum(computed.p, 1)}г</span>
                    <span className="text-warn">Ж {fmtNum(computed.f, 1)}г</span>
                    <span className="text-sky-glow">У {fmtNum(computed.c, 1)}г</span>
                    {computed.weight > 0 && (
                      <span className="text-ink-dim ml-auto">{computed.weight}г</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* STICKY КНОПКА — всегда видна, не скроллится */}
            <div className="shrink-0 px-5 pt-3 pb-5 border-t border-line/30 bg-bg">
              <div className="max-w-md mx-auto">
                <Button
                  fullWidth
                  size="lg"
                  disabled={saving || computed.cal <= 0}
                  onClick={handleSave}
                  icon={saving ? <Loader2 size={18} className="animate-spin" /> : undefined}
                >
                  {saving
                    ? 'Сохраняем…'
                    : dayOffset !== 0
                      ? `Добавить ${dayPhrase(dayOffset)} · ${fmtNum(computed.cal)} ккал`
                      : `Добавить в дневник · ${fmtNum(computed.cal)} ккал`}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
